import { formatNumber, getFileExtension } from 'shared/utils';
import { MS_PER_DAY } from 'shared/constants/date.constants';
import { ISSUE_REASONS, RESULT_VARIANTS, TAB_STATUSES } from './upload.types';
import type { ImportResult, ResultVariant, SheetIssue, TabAnalysis } from './upload.types';

const ACCEPTED_EXTENSIONS = ['xlsx'];

// Single source of truth for the file picker's `accept` attribute.
export const ACCEPTED_FILE_TYPES = ACCEPTED_EXTENSIONS.map(extension => `.${extension}`).join(',');

const C8_TAB_MARKER = 'c8';

const BYTES_PER_KILOBYTE = 1024;

// Accepts only a plausible address so a stray cell like "see @john" is not
// mistaken for an attendee.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const JUST_NOW_THRESHOLD_MS = 45_000;
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

export const isAcceptedFile = (fileName: string) =>
  ACCEPTED_EXTENSIONS.includes(getFileExtension(fileName));

export const isAttendeeEmail = (value: string) => EMAIL_PATTERN.test(value);

export const formatFileSize = (bytes: number) => `${(bytes / BYTES_PER_KILOBYTE).toFixed(1)} KB`;

const isAutoSelectedTab = (sheetName: string) => sheetName.toLowerCase().includes(C8_TAB_MARKER);

export const autoSelectTabs = (sheetNames: string[]) => sheetNames.filter(isAutoSelectedTab);

// C8 tabs sit near the end of a real workbook, so without this the collapsed
// grid shows ten tabs that are all unticked and the auto-selection looks broken.
export const orderTabsForDisplay = (sheetNames: string[]) => [
  ...sheetNames.filter(isAutoSelectedTab),
  ...sheetNames.filter(sheetName => !isAutoSelectedTab(sheetName)),
];

export const countSkippedRows = (tabs: TabAnalysis[]) =>
  tabs.reduce((total, tab) => total + tab.skippedCount, 0);

export const countExcludedTabs = (tabs: TabAnalysis[]) =>
  tabs.filter(tab => tab.status === TAB_STATUSES.EXCLUDED).length;

export const collectIssues = (tabs: TabAnalysis[]): SheetIssue[] => tabs.flatMap(tab => tab.issues);

export const resolveResultVariant = (result: ImportResult): ResultVariant =>
  result.skippedCount > 0 || result.tabsSkipped > 0
    ? RESULT_VARIANTS.PARTIAL
    : RESULT_VARIANTS.SUCCESS;

export const formatRelativeTime = (timestamp: number, now: number) => {
  const elapsed = Math.max(0, now - timestamp);

  if (elapsed < JUST_NOW_THRESHOLD_MS) return { key: 'SHIFTS_JUST_NOW' };
  if (elapsed < HOUR_MS) {
    return { key: 'SHIFTS_MINUTES_AGO', count: Math.max(1, Math.floor(elapsed / MINUTE_MS)) };
  }
  if (elapsed < MS_PER_DAY)
    return { key: 'SHIFTS_HOURS_AGO', count: Math.floor(elapsed / HOUR_MS) };
  return { key: 'SHIFTS_DAYS_AGO', count: Math.floor(elapsed / MS_PER_DAY) };
};

// The result card and its toast must always say the same thing. Takes the
// variant so callers that already resolved it do not resolve it twice.
export const formatResultTitle = (
  result: ImportResult,
  variant: ResultVariant,
  lang: Record<string, string>,
) =>
  (variant === RESULT_VARIANTS.PARTIAL ? lang.SHIFTS_RESULT_PARTIAL : lang.SHIFTS_RESULT_SUCCESS)!
    .replace('{count}', formatNumber(result.importedCount))
    .replace('{skipped}', formatNumber(result.skippedCount));

export const issueReasonKey = (reason: SheetIssue['reason']) =>
  reason === ISSUE_REASONS.USER_NOT_FOUND
    ? 'SHIFTS_ISSUE_USER_NOT_FOUND'
    : 'SHIFTS_ISSUE_INVALID_ATTENDEE';
